import {computed, Ref} from "vue";
import {agendaApi} from "@/container";
import useOrganizationId from "@/core/composables/useOrganizationId";
import useSwr from "@/core/composables/swr/useSwr";
import {cacheKey} from "@/lib/cache";
import {AgendaBirthday} from "@/core/interfaces/AgendaBirthday";
import {birthdayDateKey} from "@/agenda/lib/birthdays";

/**
 * Cached payload, stamped with the key it was fetched for.
 */
interface BirthdayPayload {
    key: string;
    birthdays: AgendaBirthday[];
}

/**
 * The targeted patient birthdays of the agenda's window.
 *
 * Fetched on demand rather than carried by the agenda window like the renewal
 * reminders are: the week and month overlay is off by default and the day
 * panel opens on request, so birthdays must not cost a patient join on every
 * navigation. `enabled` is what gates it — turning the overlay back on for a
 * window already read serves the swrv cache without a new request.
 */
const useAgendaBirthdays = (range: Ref<{ from: Date; to: Date } | null>, enabled: Ref<boolean>) => {
    const organizationId = useOrganizationId();

    /**
     * Request params, single source for the key and the fetcher so the two
     * can never drift apart. Dates, not instants: the server matches on the
     * day, and a time component would drop the birthdays of that very day.
     */
    const params = computed(() => {
        if (!range.value) {
            return null;
        }

        return {
            from: birthdayDateKey(range.value.from),
            to: birthdayDateKey(range.value.to)
        };
    });

    const key = computed(() => {
        if (!enabled.value || !organizationId.value || !params.value) {
            return null;
        }

        return cacheKey(`organizations/${organizationId.value}/birthdays`, params.value);
    });

    const fetchData = async (): Promise<BirthdayPayload> => {
        const requestedKey = key.value;

        if (!organizationId.value || !params.value || !requestedKey) {
            return {key: '', birthdays: []};
        }

        return {
            key: requestedKey,
            birthdays: await agendaApi().birthdays(organizationId.value, params.value)
        };
    };

    const {data: payload, error, isLoading, mutate} = useSwr<BirthdayPayload>(
        () => key.value,
        () => fetchData()
    );

    /**
     * Contents of the displayed window, and nothing else: swrv keeps the
     * previous key's data on a key change, and rendering it would put last
     * month's birthdays under this month's header.
     */
    const birthdays = computed<AgendaBirthday[]>(() =>
        payload.value?.key === key.value ? payload.value.birthdays : []);

    /** The birthdays of a given day, the panel's and the timeline's unit. */
    const birthdaysOn = (day: Date): AgendaBirthday[] => {
        const date = birthdayDateKey(day);

        return birthdays.value.filter(birthday => birthday.celebrated_on === date);
    };

    /**
     * Re-read the window. Called after a send: the voucher state decides
     * whether a patient can still be served, so a stale list would keep
     * offering a gift that already went out.
     */
    const refresh = (): Promise<unknown> => mutate();

    return {
        birthdays,
        birthdaysOn,
        refresh,
        loading: computed<boolean>(() => enabled.value && isLoading.value),
        failed: computed<boolean>(() => Boolean(error.value))
    };
};

export default useAgendaBirthdays;
